"use client";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useRef } from "react"
import { Plus, Minus } from "lucide-react"
import { addToCart, decreaseQuantity, increaseQuantity } from "../config/redux/slices/cartSlice";
import { toast } from "react-toastify";


const AddToCartBanner = ({ option, selectedSubcategory, selectedSubcategoryId, Discount, offering, fullWidth = false }) => {
  const dispatch = useDispatch();
  const cart = useSelector(state => state.cart.items);
  const cartItem = cart.find(item => item.id === option.id);
  const isFull = option?.status === "full" || option?.isLive === false;
  
  // Animation states
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState('up'); // 'up' or 'down'
  const prevQuantityRef = useRef(cartItem?.quantity || 0);

  // Sync animation across all instances when Redux quantity changes
  useEffect(() => {
    const currentQty = cartItem?.quantity || 0;
    const prevQty = prevQuantityRef.current;
    
    if (currentQty !== prevQty && prevQty !== 0 && currentQty !== 0) {
      // Quantity changed, trigger animation
      setAnimationDirection(currentQty > prevQty ? 'up' : 'down');
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 200);
    }
    
    prevQuantityRef.current = currentQty;
  }, [cartItem?.quantity]);
  
  const hourlyOptions = [
    "30 Min Express",
    "1 Hour Standard",
    "1.5 Hour Plus",
    "2 Hour Routine",
    "3 Hour Complete",
    "30-Minute Cleaning",
    "1-Hour Cleaning",
    "2-Hour Cleaning",
    "3-Hour Cleaning",
  ];

  const isHourly = hourlyOptions.includes(option.name);
  const hasDifferentHourlyInCart = cart.some((item) =>
    hourlyOptions.includes(item.name) && item.id !== option.id
  );
  const isBlocked = isHourly && hasDifferentHourlyInCart;

  const carWashOptions = ["Hatchback / Sedan", "SUV / MUV", "Luxury Car", "Truck / Bus"];
  const bikeWashOptions = ["Bike / Scooter"];
  const isCarWash = carWashOptions.includes(option.name);
  const isBikeWash = bikeWashOptions.includes(option.name);

  // Check if option is Utensils (max quantity 110)
  const isUtensils = option.name === "Utensils";
  const isMaxQuantity = (
    (isCarWash && cartItem?.quantity >= 3) ||
    (isBikeWash && cartItem?.quantity >= 5) ||
    (isUtensils && cartItem?.quantity >= 110)
  );
  const isIncrementDisabled = isFull || isMaxQuantity || (isHourly && (cartItem?.quantity || 0) >= 1);

  const labelMap = {
    Utensils: "utensil",
    Bathroom: "bathrooms",
    Staircase: "floors",
    "Fan Cleaning": "fans",
    "Window Cleaning": "windows",
    Balcony: "balconies",
    "Water Tank Cleaning": "tanks",
    "Kitchen Prep": "minutes",
    "Kitchen": "kitchen",
    "Fridge Cleaning": "fridge",
    "Sofa Cleaning": "sofa",
    "Carpet Cleaning": "carpet",
    "Roof Top Cleaning": "roof",
    "Hatchback / Sedan": "smallcar",
    "SUV / MUV": "bigcar",
    "Luxury Car": "car",
    "Truck / Bus": "vehicle",
    "Bike / Scooter": "bike",
    "30 Min Express": "30 mins",
    "1 Hour Standard": "1 hour",
    "1.5 Hour Plus": "1.5 hour",
    "2 Hour Routine": "2 hours",
    "3 Hour Complete": "3 hours",
    "30-Minute Cleaning":"30 mins",
    "1-Hour Cleaning":"1 hour",
    "2-Hour Cleaning":"2 hours",
   "3-Hour Cleaning":"3 hours",
    "Kitchen Deep Cleaning" :"kitchens",
   "Bathroom Deep Cleaning" :"bathrooms",
   "Bedroom Deep Cleaning" :"bedrooms",
   "Dinning room  Deep Cleaning" :"rooms",
   "Cabients Deep Cleaning" :"kitchen",
   "Almirah Deep Cleaning" :"almirahs",
   "Balcony Deep Cleaning" :"balconies",
   "Rooftop Deep Cleaning" :"rooftops",
   "Front Yard Deep Cleaning" :"frontyards",
    "Backyard Deep Cleaning" :"backyards",
    "Chimney Deep Cleaning" :"chimneys",
    "Cabinets Deep Cleaning":"cabinets",
    
  };

  useEffect(() => {
    if (cart && cart.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cart));
    } else {
      localStorage.removeItem('cart');
    }
  }, [cart]);

  return (
    <>
      <div className={`z-10 ${fullWidth ? "w-full" : ""}`}>
        {(!cartItem || cartItem.quantity === 0) ? (
          // Show "+" icon button when not in cart or quantity is zero
          <button
            onClick={() => {
              if (isBlocked) {
                toast.error("Only one hourly service can be added at a time. Please remove the existing hourly service first.");
                return;
              }
              dispatch(addToCart({
                ...option,
                selectedSubcategory,
                Discount,
                offering,
                selectedSubcategoryId,
                serviceType: option?.serviceType,
                slug: option?.slug,
                categoryRef: option?.categoryRef,
              }));
            }}
            disabled={isFull || isBlocked}
            className={fullWidth
              ? `h-11 rounded-[12px] w-full border font-bold flex items-center justify-center ${isFull || isBlocked ? "border-gray-300 bg-gray-100 text-gray-400" : "border-[#00489b] bg-[#00489b] text-white"}`
              : `h-8 rounded-[6px] w-[100px] border font-bold flex items-center justify-center ${isFull || isBlocked ? "border-gray-300 bg-gray-100 text-gray-400" : "border-[#00489b] bg-white text-[#00489b]"}`}
          >
            {fullWidth ? "Add to Cart" : "Add"}
          </button>
        ) : (
          // Show increment-decrement UI if already in cart and quantity > 0
          <div className={fullWidth
            ? "relative flex flex-col items-center justify-center h-[58px] w-full border border-[#00489b] rounded-[14px] bg-gradient-to-b from-white to-[#f7fbff] px-3 py-1 shadow-[0_6px_16px_rgba(0,72,155,0.10)]"
            : "relative flex flex-col items-center justify-center h-8 w-[100px] border border-[#00489b] rounded-[6px] bg-white"}
          >
            {/* Top row with - qty + */}
            <div className={fullWidth ? "flex items-center justify-between w-full" : "flex items-center justify-center w-[90px]"}>
              <button
                onClick={() => {
                  dispatch(decreaseQuantity(option.id));
                }}
                disabled={isFull}
                className={`font-bold flex items-center justify-center transition-transform duration-150 active:scale-90 ${fullWidth ? "w-8 h-8 rounded-full border border-[#d9e6f7] bg-white" : "w-5 h-5 rounded-md"} ${isFull ? "text-gray-400" : "text-[#00489b]"}`}
              >
                <Minus className={fullWidth ? "w-4 h-4" : "w-5 h-5"} />
              </button>
              <div className={fullWidth ? "relative w-12 h-6 overflow-hidden" : "relative w-8 h-6 overflow-hidden"}>
                <span 
                  className={`absolute inset-0 font-bold text-[15px] text-[#00489b] flex items-center justify-center transition-all duration-200 ${
                    isAnimating 
                      ? animationDirection === 'up' 
                        ? '-translate-y-full opacity-0' 
                        : 'translate-y-full opacity-0'
                      : 'translate-y-0 opacity-100'
                  }`}
                >
                  {cartItem.quantity}
                </span>
              </div>
              <button
                onClick={() => {
                  if (isMaxQuantity) {
                    if (isUtensils) {
                      toast.error("Maximum quantity of 110 utensils reached.");
                    } else if (isBikeWash) {
                      toast.error("Maximum quantity of 5 reached for Bike / Scooter.");
                    } else {
                      toast.error("Maximum quantity of 3 reached for this item.");
                    }
                    return;
                  }
                  if (isHourly && (cartItem?.quantity || 0) >= 1) {
                    toast.error("Only one hourly service can be added at a time.");
                    return;
                  }
                  dispatch(increaseQuantity(option.id));
                }}
                disabled={isIncrementDisabled}
                className={`font-bold flex items-center justify-center transition-transform duration-150 active:scale-90 ${fullWidth ? "w-8 h-8 rounded-full border" : "w-5 h-5 rounded-md"} ${isIncrementDisabled ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed" : fullWidth ? "border-[#d9e6f7] bg-white text-[#00489b]" : "text-[#00489b]"}`}
              >
                <Plus className={fullWidth ? "w-4 h-4" : "w-5 h-5 z-25"} />
              </button>
            </div>

            {/* Text below line */}
            {fullWidth ? (
              <div className="text-[11px] text-[#64748b] font-semibold leading-none mt-0.5">
                {labelMap[option.name] || "rooms"}
              </div>
            ) : (
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex justify-center items-center text-center px-2 bg-white z-20 font-bold text-[12px] text-[#666]">
                {labelMap[option.name] || "rooms"}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
export default AddToCartBanner;